import {
  Directive,
  Input,
  OnInit,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { PermissionService, UserRole } from '../services/permission.service';

// ─────────────────────────────────────────────────────────────────────────────
// *hasPermission="'users.manage_groups'"
// *hasPermission="['users.create', 'users.edit']"
// *hasRole="UserRole.SCA_SUPER_ADMIN"
// *hasRole="[UserRole.SCA_SUPER_ADMIN, UserRole.SCA_IES_ADMIN]"
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Directive structurelle basée sur une ACTION nommée.
 *
 * Usage :
 *   <li *hasPermission="'users.manage_groups'">...</li>
 *   <button *hasPermission="['users.create', 'users.edit']">...</button>
 */
@Directive({
  selector: '[hasPermission]',
  standalone: true,
})
export class HasPermissionDirective implements OnInit, OnDestroy {

  @Input('hasPermission') permission!: string | string[];

  private tpl  = inject(TemplateRef<any>);
  private vcr  = inject(ViewContainerRef);
  private svc  = inject(PermissionService);
  private sub!: Subscription;
  private rendered = false;

  ngOnInit(): void {
    this.sub = this.svc.context.subscribe(() => this.updateView());
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private updateView(): void {
    const allowed = Array.isArray(this.permission)
      ? this.permission.every(p => this.svc.can(p))   // AND sémantique sur un tableau
      : this.svc.can(this.permission);

    if (allowed && !this.rendered) {
      this.vcr.createEmbeddedView(this.tpl);
      this.rendered = true;
    } else if (!allowed && this.rendered) {
      this.vcr.clear();
      this.rendered = false;
    }
  }
}

/**
 * Directive structurelle basée sur un RÔLE.
 *
 * Usage :
 *   <div *hasRole="'SUPER_ADMIN'">...</div>
 *   <div *hasRole="['SUPER_ADMIN', 'IES_ADMIN']">...</div>
 */
@Directive({
  selector: '[hasRole]',
  standalone: true,
})
export class HasRoleDirective implements OnInit, OnDestroy {

  @Input('hasRole') roles!: UserRole | UserRole[] | string | string[];

  private tpl  = inject(TemplateRef<any>);
  private vcr  = inject(ViewContainerRef);
  private svc  = inject(PermissionService);
  private sub!: Subscription;
  private rendered = false;

  ngOnInit(): void {
    this.sub = this.svc.context.subscribe(() => this.updateView());
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private updateView(): void {
    const list   = Array.isArray(this.roles) ? this.roles : [this.roles];
    const allowed = this.svc.hasAnyRole(...(list as UserRole[]));

    if (allowed && !this.rendered) {
      this.vcr.createEmbeddedView(this.tpl);
      this.rendered = true;
    } else if (!allowed && this.rendered) {
      this.vcr.clear();
      this.rendered = false;
    }
  }
}